import { useEffect, useRef, useReducer } from "react";
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from "body-scroll-lock";
import useMedia from "utils/useMedia";

type NavState = {
  navIsExpanded: boolean;
  searchIsExpanded: boolean;
  overlayOpen: boolean;
};

type NavAction =
  | { type: "OPEN_EXPANDED_NAV" }
  | { type: "CLOSE_EXPANDED_NAV" }
  | { type: "OPEN_DESKTOP_SEARCH" }
  | { type: "CLOSE_DESKTOP_SEARCH" }
  | { type: "OPEN_MOBILE_SEARCH" }
  | { type: "CLOSE_MOBILE_SEARCH" };

/**
 * Instead of directly updating nav state, we use a reducer to dispatch
 * different types of actions that put the nav in a discrete state. This
 * helps prevent unwanted and inconsistent nav states.
 */
const navReducer = (state: NavState, action: NavAction) => {
  switch (action.type) {
    case "OPEN_EXPANDED_NAV":
      return {
        navIsExpanded: true,
        searchIsExpanded: false,
        overlayOpen: true,
      };
    case "CLOSE_EXPANDED_NAV":
      return {
        navIsExpanded: false,
        searchIsExpanded: false,
        overlayOpen: false,
      };
    case "OPEN_DESKTOP_SEARCH":
      return {
        navIsExpanded: false,
        searchIsExpanded: true,
        overlayOpen: true,
      };
    case "CLOSE_DESKTOP_SEARCH":
      return {
        navIsExpanded: false,
        searchIsExpanded: false,
        overlayOpen: false,
      };
    case "OPEN_MOBILE_SEARCH":
      return {
        navIsExpanded: true,
        searchIsExpanded: true,
        overlayOpen: true,
      };
    case "CLOSE_MOBILE_SEARCH":
      return {
        navIsExpanded: true,
        searchIsExpanded: false,
        overlayOpen: true,
      };
  }
};

const useNav = () => {
  const [isMobile, isDesktop] = useMedia(
    "(max-width: 975px)",
    "(min-width: 976px)"
  );

  const [
    { navIsExpanded, searchIsExpanded, overlayOpen },
    dispatch,
  ] = useReducer(navReducer, {
    navIsExpanded: false,
    searchIsExpanded: false,
    overlayOpen: false,
  });

  const navRef = useRef<HTMLDivElement>(null!);
  const expandedNavRef = useRef<HTMLDivElement>(null!);
  const desktopSearchRef = useRef<HTMLDivElement>(null!);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null!);
  const searchButtonRef = useRef<HTMLButtonElement>(null!);

  const openNav = () => dispatch({ type: "OPEN_EXPANDED_NAV" });
  const closeNav = () => dispatch({ type: "CLOSE_EXPANDED_NAV" });
  const openDesktopSearch = () => dispatch({ type: "OPEN_DESKTOP_SEARCH" });
  const closeDesktopSearch = () => dispatch({ type: "CLOSE_DESKTOP_SEARCH" });
  const openMobileSearch = () => dispatch({ type: "OPEN_MOBILE_SEARCH" });
  const closeMobileSearch = () => dispatch({ type: "CLOSE_MOBILE_SEARCH" });

  /**
   * Disable body scrolling when the nav is open, and restore
   * scroll to the original scroll position when the nav is
   * closed or the component unmounts. The `body-scroll-lock`
   * library is a better than just adding `overflow: hidden` to
   * the`body` element because it handles browser and mobile
   * inconsistencies
   *
   * See the following for more context:
   * https://github.com/willmcpo/body-scroll-lock
   * https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
   */
  useEffect(() => {
    if (overlayOpen) {
      disableBodyScroll(navRef.current);
    } else {
      enableBodyScroll(navRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [overlayOpen]);

  /**
   * Close the expanded nav and search box when a user clicks on the Escape key
   */
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const activeElement = document.activeElement;

        if (navIsExpanded && !searchIsExpanded) {
          closeNav();
          /**
           * if focus is inside of the expanded nav, we want to
           * return focus back to the hamburger button on Escape
           */
          if (expandedNavRef.current.contains(activeElement)) {
            hamburgerButtonRef.current.focus({ preventScroll: true });
          }
        }

        if (searchIsExpanded && isDesktop) {
          closeDesktopSearch();
          /**
           * if focus is inside of the search box, we want to
           * return focus back to the search button on Escape
           */
          if (desktopSearchRef.current.contains(activeElement)) {
            searchButtonRef.current.focus({ preventScroll: true });
          }
        }

        if (searchIsExpanded && isMobile) {
          /**
           * On mobile, the react-focus-lock package handles bringing focus back to the
           * button that triggered it, so we don't need custom logic to do it on Escape
           */
          closeMobileSearch();
        }
      }
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [navIsExpanded, searchIsExpanded, isDesktop, isMobile]);

  return {
    refs: {
      navRef,
      expandedNavRef,
      desktopSearchRef,
      searchButtonRef,
      hamburgerButtonRef,
    },
    state: {
      navIsExpanded,
      searchIsExpanded,
      overlayOpen,
      isDesktop,
      isMobile,
    },
    actions: {
      openNav,
      closeNav,
      openDesktopSearch,
      closeDesktopSearch,
      openMobileSearch,
      closeMobileSearch,
    },
  };
};

export default useNav;
